import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('hearclear_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const reqUrl = error.config?.url || '';
      const isLoginReq = reqUrl.includes('/auth/login');
      // Don't hard-redirect on the login request itself (or when already on /login)
      // so the login page can render an inline "Invalid credentials" error.
      const onLoginPage = window.location.pathname.startsWith('/login');
      // Only redirect admin users, not employee or clinic portal
      if (!isLoginReq && !onLoginPage
          && !window.location.pathname.startsWith('/employee')
          && !window.location.pathname.startsWith('/clinic')) {
        localStorage.removeItem('hearclear_token');
        localStorage.removeItem('hearclear_user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Separate axios instance for employee portal (uses emp_token)
const empApi = axios.create({ baseURL: API_BASE, headers: { 'Content-Type': 'application/json' } });
empApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('emp_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
empApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && window.location.pathname.startsWith('/employee')) {
      localStorage.removeItem('emp_token');
      localStorage.removeItem('emp_data');
    }
    return Promise.reject(error);
  }
);

// Separate axios instance for clinic portal (uses clinic_token)
const clinicApi = axios.create({ baseURL: API_BASE, headers: { 'Content-Type': 'application/json' } });
clinicApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('clinic_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Corporate office: pass selected clinic ID
  const actingClinicId = localStorage.getItem('acting_clinic_id');
  if (actingClinicId) config.headers['X-Acting-Clinic-Id'] = actingClinicId;
  return config;
});
clinicApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && window.location.pathname.startsWith('/clinic')) {
      localStorage.removeItem('clinic_token');
      localStorage.removeItem('clinic_data');
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  getMe: () => api.get('/auth/me'),
  register: (userData) => api.post('/auth/register', userData),
};

// Users API
export const usersAPI = {
  list: () => api.get('/users'),
  create: (data) => api.post('/auth/register', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  resetPassword: (id, new_password) => api.post(`/users/${id}/reset-password`, { new_password }),
  toggleActive: (userId) => api.put(`/users/${userId}/toggle-active`),
};

// Roles & Permissions (RBAC)
export const rolesAPI = {
  list: () => api.get('/roles'),
  permissionSchema: () => api.get('/roles/permission-schema'),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  remove: (id) => api.delete(`/roles/${id}`),
};

// Employee access assignment (role + clinics)
export const accessAPI = {
  clinics: () => api.get('/access/clinics'),
  employees: (params) => api.get('/access/employees', { params }),
  updateEmployee: (employeeNumber, data) => api.put(`/access/employees/${employeeNumber}`, data),
};

// Dashboard API
export const dashboardAPI = {
  getDailySummary: (date, source, start_date, end_date) => api.get('/dashboard/daily-summary', { params: { date, source, start_date, end_date } }),
  getMonthlySummary: (month, year, source) => api.get('/dashboard/monthly-summary', { params: { month, year, source } }),
  getClinicPerformance: (params) => api.get('/dashboard/clinic-performance', { params }),
  getOverview: (params) => api.get('/dashboard/overview', { params }),
};

// Legacy Zoho Data API (Read-Only)
export const legacyAPI = {
  getSummary: () => api.get('/legacy/summary'),
  getInvoices: (params) => api.get('/legacy/invoices', { params }),
  getSalesOrders: (params) => api.get('/legacy/sales-orders', { params }),
  getCreditNotes: () => api.get('/legacy/credit-notes'),
};

// Zoho Historical Import (one-time bulk pull from Zoho Books API)
export const zohoHistoricalAPI = {
  testConnection: () => api.get('/admin/zoho-historical/test-connection'),
  wipe: () => api.post('/admin/zoho-historical/wipe', { confirm: 'WIPE-NON-HR-DATA' }),
  start: (date_start, date_end) => api.post('/admin/zoho-historical/start', { date_start, date_end }),
  status: () => api.get('/admin/zoho-historical/status'),
  // Pass-2 enrichment
  enrichItems: () => api.post('/admin/zoho-historical/enrich/items'),
  enrichInvoices: (batch_size = 500, max_calls = 9000) => api.post('/admin/zoho-historical/enrich/invoices', { batch_size, max_calls }),
  enrichSalesOrders: (batch_size = 500, max_calls = 9000) => api.post('/admin/zoho-historical/enrich/sales-orders', { batch_size, max_calls }),
  enrichBillSerials: () => api.post('/admin/zoho-historical/enrich/bill-serials'),
  enrichSerialsFromDescriptions: () => api.post('/admin/zoho-historical/enrich/serials-from-descriptions'),
  enrichStatus: () => api.get('/admin/zoho-historical/enrich/status'),
  // Backfills
  backfillSoLeadSource: () => api.post('/admin/zoho-historical/backfill/so-lead-source'),
  backfillInvoiceAudiologist: () => api.post('/admin/zoho-historical/backfill/invoice-audiologist'),
  crossrefSerials: () => api.post('/admin/zoho-historical/backfill/crossref-serials'),
  fifoAssignSerials: () => api.post('/admin/zoho-historical/backfill/fifo-assign-serials'),
  fifoAssignVbSerials: () => api.post('/admin/zoho-historical/backfill/fifo-assign-vb-serials'),
  syncTransferOrders: () => api.post('/admin/zoho-historical/sync/transfer-orders'),
  backfillInvoiceSerialsPC: () => api.post('/admin/zoho-historical/backfill/invoice-serials-product-code'),
  enrichVendorBillsDetail: (limit = 500) => api.post(`/admin/zoho-historical/enrich/vendor-bills-detail?limit=${limit}`),
  backfillDiscountKey: () => api.post('/admin/zoho-historical/backfill/line-item-discount-key'),
  backfillGstSplit: () => api.post('/admin/zoho-historical/backfill/gst-split'),
  remapClinics: () => api.post('/admin/zoho-historical/backfill/remap-clinics'),
  dedupeCustomers: () => api.post('/admin/zoho-historical/customers/dedupe'),
  // Pull every active serial directly from Zoho's official /items/serialnumbers endpoint
  pullActiveSerials: () => api.post('/admin/zoho-historical/pull/active-serials'),
  pullActiveSerialsStatus: () => api.get('/admin/zoho-historical/pull/active-serials/status'),
  enrichCreditNotes: () => api.post('/admin/zoho-historical/enrich/credit-notes-detail'),
  enrichVendorBills: (limit = 2000) => api.post(`/admin/zoho-historical/enrich/vendor-bills-detail?limit=${limit}`),
  stampTransferSerials: () => api.post('/admin/zoho-historical/backfill/transfer-serials-stamp'),
  reconRun: () => api.post('/admin/zoho-historical/recon/run'),
  reconStatus: () => api.get('/admin/zoho-historical/recon/status'),
  reconPerRecord: () => api.get('/admin/zoho-historical/recon/per-record'),
  // Incremental sync — pulls only what changed since the last sync
  incrementalStart: (since = null, modules = null) => api.post('/admin/zoho-historical/incremental/start', { since, modules }),
  incrementalStatus: () => api.get('/admin/zoho-historical/incremental/status'),
};

// Zoho Dump Import — merge an offline Zoho export (.xls) carrying real serials & full line items
export const zohoDumpAPI = {
  status: () => api.get('/admin/zoho-dump/status'),
  start: (modules = null) => api.post('/admin/zoho-dump/start', { modules }),
  upload: (formData) => api.post('/admin/zoho-dump/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadChunk: (formData) => api.post('/admin/zoho-dump/upload-chunk', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadFinalize: (formData) => api.post('/admin/zoho-dump/upload-finalize', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  incrementalStatus: () => api.get('/admin/zoho-dump/incremental/status'),
  incrementalFinalize: (formData) => api.post('/admin/zoho-dump/incremental/upload-finalize', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  incrementalStart: () => api.post('/admin/zoho-dump/incremental/start'),
};

// Inventory Bulk Upload
export const inventoryBulkAPI = {
  upload: (formData) => api.post('/inventory/bulk-upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadStock: (formData) => api.post('/inventory/bulk-upload-stock', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Chart of Accounts API
export const accountsAPI = {
  list: (accountType) => api.get('/accounts', { params: { account_type: accountType } }),
  create: (accountData) => api.post('/accounts', accountData),
  get: (code) => api.get(`/accounts/${code}`),
};

// Journal Entries API
export const journalAPI = {
  list: (params) => api.get('/journal-entries', { params }),
  create: (entryData) => api.post('/journal-entries', entryData),
  generateFromVendors: (params) => api.post('/journal-entries/generate-from-vendors', null, { params }),
};

// Invoices API
export const invoicesAPI = {
  list: (params) => api.get('/invoices', { params }),
  export: (params) => api.get('/invoices/export', { params, responseType: 'blob' }),
  get: (invoiceId) => api.get(`/invoices/${invoiceId}`),
  getMeta: () => api.get('/invoices-meta'),
  sync: (invoiceData) => api.post('/invoices/sync', invoiceData),
  delete: (invoiceId) => api.delete(`/admin/invoices/${invoiceId}`),
  update: (invoiceId, data) => api.put(`/admin/invoices/${invoiceId}`, data),
  audit: (invoiceId) => api.get(`/admin/invoices/${invoiceId}/audit`),
  changeSerial: (invoiceId, data) => api.post(`/admin/invoices/${invoiceId}/change-serial`, data),
  bulkUpload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/admin/invoices/bulk-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  downloadTemplate: () => api.get('/admin/invoices/bulk-upload/template', { responseType: 'blob' }),
};

// Payments API
export const paymentsAPI = {
  list: (params) => api.get('/payments', { params }),
  sync: (paymentData) => api.post('/payments/sync', paymentData),
  // Payment operations (Phase 2)
  edit: (id, data) => api.put(`/admin/payments/${id}`, data),
  reconcile: (id, reconciled) => api.post(`/admin/payments/${id}/reconcile?reconciled=${reconciled}`),
  remove: (id) => api.delete(`/admin/payments/${id}`),
  dissociateCredit: (id) => api.post(`/admin/payments/${id}/dissociate-credit`),
  refund: (id, data) => api.post(`/admin/payments/${id}/refund`, data),
  receiptPdf: (id) => api.get(`/admin/payments/${id}/receipt-pdf`, { responseType: 'blob' }),
  customerCredit: (customerId) => api.get(`/admin/customers/${customerId}/credit-balance`),
};

// Banks / Accounts master (#6)
export const banksAPI = {
  list: (params) => api.get('/banks', { params }),
  create: (data) => api.post('/banks', data),
  update: (id, data) => api.put(`/banks/${id}`, data),
  remove: (id) => api.delete(`/banks/${id}`),
  seedFromPayments: () => api.post('/banks/seed-from-payments'),
};

// Cash Ledger (admin, location-wise) (#5)
export const cashLedgerAPI = {
  summary: () => api.get('/admin/cash-ledger/summary'),
  list: (params) => api.get('/admin/cash-ledger', { params }),
};

// Report exports (#16 ledger, #19 purchase)
// Trigger a browser download from a blob response
export const downloadBlob = (res, fallbackName) => {  const disp = res.headers?.['content-disposition'] || '';
  const m = disp.match(/filename="?([^"]+)"?/);
  const name = m ? m[1] : fallbackName;
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click();
  a.remove(); window.URL.revokeObjectURL(url);
};

// Bank Reconciliation API
export const reconciliationAPI = {
  uploadStatement: (formData, bankName, accountNumber) => 
    api.post(`/bank/upload-statement?bank_name=${encodeURIComponent(bankName)}&account_number=${encodeURIComponent(accountNumber)}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  listStatements: () => api.get('/bank/statements'),
  downloadStatement: (statementId) => api.get(`/bank/statements/${statementId}/download`, { responseType: 'blob' }),
  listTransactions: (params) => api.get('/bank/transactions', { params }),
  autoMatch: () => api.post('/bank/auto-reconcile-all'),
  manualMatch: (matchData) => api.post('/reconciliation/manual-match', matchData),
  getSummary: () => api.get('/reconciliation/summary'),
};

// GST API
export const gstAPI = {
  listEntries: (params) => api.get('/gst/entries', { params }),
  getGSTR1Summary: (month, year) => api.get('/gst/gstr1-summary', { params: { month, year } }),
  getGSTR3BSummary: (month, year) => api.get('/gst/gstr3b-summary', { params: { month, year } }),
  getRegistrations: () => api.get('/gst/registrations'),
  getReturns: (params) => api.get('/gst/returns', { params }),
  uploadGSTR1: (formData, gstin) => api.post(`/gst/upload-gstr1?gstin=${encodeURIComponent(gstin)}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadGSTR3B: (formData, gstin) => api.post(`/gst/upload-gstr3b?gstin=${encodeURIComponent(gstin)}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  reconcile: (gstin, period) => api.get(`/gst/reconcile/${gstin}/${period}`),
  enrichInvoices: (batchSize) => api.post(`/gst/enrich-invoices?batch_size=${batchSize || 50}`),
  enrichmentStatus: () => api.get('/gst/enrichment-status'),
  verifyGstin: (gstin) => api.get(`/gst/verify/${gstin}`),
};

// Customers API
export const customersAPI = {
  list: (params) => api.get('/customers', { params }),
  get: (customerId) => api.get(`/customers/${customerId}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  transactions: (id, params) => api.get(`/customers/${id}/transactions`, { params }),
  drilldown: (params) => api.get('/customers/drilldown', { params }),
  bulkUpload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/customers/bulk-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// TDS API
export const tdsAPI = {
  listEntries: (params) => api.get('/tds/entries', { params }),
  createEntry: (entryData) => api.post('/tds/entries', entryData),
  getSummary: (quarter, year) => api.get('/tds/summary', { params: { quarter, year } }),
};

// Compliance API
export const complianceAPI = {
  listTasks: (params) => api.get('/compliance/tasks', { params }),
  createTask: (taskData) => api.post('/compliance/tasks', taskData),
  updateTask: (taskId, status) => api.put(`/compliance/tasks/${taskId}`, null, { params: { status } }),
  getCalendar: () => api.get('/compliance/calendar'),
};

// Reports API
export const reportsAPI = {
  purchase: (params) => api.get('/reports/purchase', { params }),
  purchaseExport: (params) => api.get('/reports/purchase/export', { params, responseType: 'blob' }),
  customerLedgerExport: (params) => api.get('/reports/customer-ledger/export', { params, responseType: 'blob' }),
  getTrialBalance: (asOfDate) => api.get('/reports/trial-balance', { params: { as_of_date: asOfDate } }),
  getProfitLoss: (params) => api.get('/reports/profit-loss', { params }),
  getBalanceSheet: (asOfDate) => api.get('/reports/balance-sheet', { params: { as_of_date: asOfDate } }),
  getCashFlow: (params) => api.get('/reports/cash-flow', { params }),
  discounts: (params) => api.get('/reports/discounts', { params }),
  discountFilterOptions: () => api.get('/reports/discounts/filter-options'),
  itemWise: (params) => api.get('/reports/item-wise', { params }),
  itemWiseFilterOptions: () => api.get('/reports/item-wise/filter-options'),
  invoiceReport: (params) => api.get('/reports/invoice-report', { params }),
  invoiceReportFilterOptions: () => api.get('/reports/invoice-report/filter-options'),
  salesOrderReport: (params) => api.get('/reports/sales-order-report', { params }),
  salesOrderReportFilterOptions: () => api.get('/reports/sales-order-report/filter-options'),
};

// AI API
export const aiAPI = {
  analyze: (request) => api.post('/ai/analyze', request),
  getAnalysis: (analysisId) => api.get(`/ai/analysis/${analysisId}`),
  getAlerts: () => api.get('/ai/alerts'),
};

// E-Invoicing (#14, MOCKED govt connection)
export const einvoiceAPI = {
  getSettings: () => api.get('/einvoice/settings'),
  updateSettings: (data) => api.put('/einvoice/settings', data),
  listCredentials: () => api.get('/einvoice/credentials'),
  connect: (data) => api.post('/einvoice/credentials/connect', data),
  disconnect: (gstin) => api.post('/einvoice/credentials/disconnect', { gstin }),
  pushToIrp: (invoiceId) => api.post(`/einvoice/invoices/${invoiceId}/push-irp`),
  cancelIrn: (invoiceId, reason) => api.post(`/einvoice/invoices/${invoiceId}/cancel-irn`, { reason }),
  getInvoiceEinvoice: (invoiceId) => api.get(`/einvoice/invoices/${invoiceId}`),
};

// Audit Logs API
export const auditAPI = {
  list: (params) => api.get('/audit-logs', { params }),
};

// Seed API
export const seedAPI = {
  createAdmin: () => api.post('/seed/admin'),
  seedChartOfAccounts: () => api.post('/seed/chart-of-accounts'),
  seedDemoData: () => api.post('/seed/demo-data'),
};

// Zoho API
export const zohoAPI = {
  sync: (request) => api.post('/zoho/sync', request),
  syncPaymentsSafe: () => api.post('/zoho/sync-payments-safe'),
  getSyncStatus: (syncId) => api.get(`/zoho/sync/${syncId}`),
  getSyncHistory: (limit) => api.get('/zoho/sync-history', { params: { limit } }),
  getRateStatus: () => api.get('/zoho/rate-status'),
  testConnection: () => api.get('/zoho/test-connection'),
};

// Vendor API
export const vendorAPI = {
  getAll: () => api.get('/vendors'),
  create: (data) => api.post('/vendors', data),
  update: (vendorId, data) => api.put(`/vendors/${vendorId}`, data),
  delete: (vendorId) => api.delete(`/vendors/${vendorId}`),
  getDetail: (vendorId) => api.get(`/vendors/${vendorId}`),
  getInvoices: (vendorId, params) => api.get(`/vendors/${vendorId}/invoices`, { params }),
  getLedger: (vendorId, params) => api.get(`/vendors/${vendorId}/ledger`, { params }),
  getPayments: (vendorId, params) => api.get(`/vendors/${vendorId}/payments`, { params }),
  recordPayment: (vendorId, data) => api.post(`/vendors/${vendorId}/payments`, data),
  editPayment: (paymentId, data) => api.put(`/vendor-payments/${paymentId}`, data),
  deletePayment: (paymentId) => api.delete(`/vendor-payments/${paymentId}`),
  getAllPayments: (params) => api.get('/vendor-payments', { params }),
  getInvoicePdf: (invoiceId) => api.get(`/vendor-invoices/${invoiceId}/pdf`, { responseType: 'blob' }),
};

// Expense API
export const expenseAPI = {
  getSummary: (params) => api.get('/expenses/summary', { params }),
};

// Email Invoice API
export const emailAPI = {
  configure: (data) => api.post('/email/configure', data),
  getStatus: () => api.get('/email/status'),
  syncInvoices: (params) => api.post('/email/sync-invoices', null, { params }),
};

// Invoice Parser API
export const parserAPI = {
  parseBatch: (params) => api.post('/vendor-invoices/parse-batch', null, { params }),
  parseAll: (params) => api.post('/vendor-invoices/parse-all', null, { params }),
  getStats: () => api.get('/vendor-parse-stats'),
  getInvoiceDetail: (invoiceId) => api.get(`/vendor-invoices/${invoiceId}`),
  getInvoicePdfUrl: (invoiceId) => `${API_BASE}/vendor-invoices/${invoiceId}/pdf`,
  downloadInvoicePdf: async (invoiceId, filename) => {
    const res = await api.get(`/vendor-invoices/${invoiceId}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename || 'invoice.pdf');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  viewInvoicePdf: async (invoiceId) => {
    const res = await api.get(`/vendor-invoices/${invoiceId}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    window.open(url, '_blank');
  },
};

// Sales Orders & Credit Notes API
export const salesCreditsAPI = {
  getSalesOrders: (params) => api.get('/sales-orders', { params }),
  getCreditNotes: (params) => api.get('/credit-notes', { params }),
  getReconciliation: (financialYear) => api.get('/revenue-reconciliation', { params: { financial_year: financialYear } }),
  convertSOToInvoice: (soId, data) => api.post(`/admin/sales-orders/${soId}/convert-to-invoice`, data || {}),
  uploadSalesOrdersExcel: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/upload-sales-orders-excel', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  uploadInvoicesExcel: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/upload-invoices-excel', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  releaseCNSerials: () => api.post('/admin/credit-notes/release-serials'),
};

// Sales Order Creation helpers
export const soCreateAPI = {
  branches: () => api.get('/billing/admin/branches'),
  audiologists: () => api.get('/billing/admin/audiologists'),
  nextSONumber: (stateCode) => api.get(`/billing/admin/next-so-number/${stateCode}`),
  nextInvNumber: (stateCode) => api.get(`/billing/admin/next-inv-number/${stateCode}`),
  create: (data) => api.post('/billing/admin/sales-orders/create', data),
  leadSources: () => api.get('/lead-sources'),
  customers: (params) => api.get('/customers', { params }),
  items: (params) => api.get('/items', { params }),
  availableSerials: (params) => api.get('/inventory/available-serials', { params }),
};

// Audiologist Master (admin)
export const audiologistMasterAPI = {
  list: () => api.get('/billing/admin/audiologist-master'),
  create: (data) => api.post('/billing/admin/audiologist-master', data),
  update: (id, data) => api.put(`/billing/admin/audiologist-master/${id}`, data),
  remove: (id) => api.delete(`/billing/admin/audiologist-master/${id}`),
  bulkDelete: (ids) => api.post('/billing/admin/audiologist-master/bulk-delete', { ids }),
};

// Vendor-Bank Reconciliation
export const reconcileAPI = {
  reconcileVendors: () => api.post('/vendors/reconcile-with-bank'),
};

// Bank Vendor Payments
export const bankVendorAPI = {
  getVendorPayments: (params) => api.get('/bank/vendor-payments', { params }),
};

// HR Module
export const hrAPI = {
  getDashboard: () => api.get('/hr/dashboard'),
  getEmployees: (params) => api.get('/hr/employees', { params }),
  getEmployee: (id) => api.get(`/hr/employees/${id}`),
  getPayroll: (params) => api.get('/hr/payroll', { params }),
  getPayrollYTD: (params) => api.get('/hr/payroll/ytd', { params }),
  getFilters: () => api.get('/hr/filters'),
  getDocuments: (empNo) => api.get(`/hr/documents/${empNo}`),
  uploadDocument: (empNo, docType, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/hr/documents/${empNo}/upload?doc_type=${encodeURIComponent(docType)}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  triggerIngest: () => api.post('/hr/ingest'),
  // Config
  getConfig: () => api.get('/hr/config'),
  createLeaveType: (data) => api.post('/hr/config/leave-types', data),
  deleteLeaveType: (code) => api.delete(`/hr/config/leave-types/${code}`),
  getDepartmentMaster: () => api.get('/hr/config/departments'),
  upsertDepartment: (data) => api.post('/hr/config/departments', data),
  deleteDepartment: (name) => api.delete(`/hr/config/departments/${encodeURIComponent(name)}`),
  // Comp-off approval
  getCompoffRequests: (params) => api.get('/hr/compoff-requests', { params }),
  approveCompoff: (id) => api.post(`/hr/compoff/${id}/approve`),
  rejectCompoff: (id, data) => api.post(`/hr/compoff/${id}/reject`, data || {}),
  getClinicLocations: () => api.get('/hr/config/clinic-locations'),
  createClinicLocation: (data) => api.post('/hr/config/clinic-locations', data),
  updateClinicLocation: (id, data) => api.put(`/hr/config/clinic-locations/${id}`, data),
  deleteClinicLocation: (id) => api.delete(`/hr/config/clinic-locations/${id}`),
  createShiftPolicy: (data) => api.post('/hr/config/shift-policies', data),
  createLatePenalty: (data) => api.post('/hr/config/late-penalties', data),
  deleteLatePenalty: (id) => api.delete(`/hr/config/late-penalties/${id}`),
  createDocCategory: (data) => api.post('/hr/config/document-categories', data),
  // Leave
  getLeaveBalances: (empNo) => api.get(`/hr/leave/balances/${empNo}`),
  applyLeave: (data) => api.post('/hr/leave/apply', data),
  approveLeave: (id) => api.post(`/hr/leave/${id}/approve`),
  rejectLeave: (id) => api.post(`/hr/leave/${id}/reject`),
  editLeave: (id, data) => api.put(`/hr/leave/${id}/edit`, data),
  getLeaveRequests: (params) => api.get('/hr/leave/requests', { params }),
  // Attendance
  checkIn: (data) => api.post('/hr/attendance/check-in', data),
  checkOut: (data) => api.post('/hr/attendance/check-out', data),
  getTodayAttendance: (params) => api.get('/hr/attendance/today', { params }),
  getAttendanceReport: (params) => api.get('/hr/attendance/report', { params }),
  getLateReport: (params) => api.get('/hr/attendance/late-report', { params }),
  getNoPunchIn: (params) => api.get('/hr/attendance/no-punch-in', { params }),
  // Drill-down
  getEmployeesByDepartment: (dept, params) => api.get(`/hr/employees/by-department/${encodeURIComponent(dept)}`, { params }),
  getEmployeesByLocation: (loc, params) => api.get(`/hr/employees/by-location/${encodeURIComponent(loc)}`, { params }),
  // Reimbursements
  getReimbursements: (params) => api.get('/hr/reimbursements', { params }),
  createReimbursement: (data) => api.post('/hr/reimbursements', data),
  approveReimbursement: (id) => api.post(`/hr/reimbursements/${id}/approve`),
  rejectReimbursement: (id, reason) => api.post(`/hr/reimbursements/${id}/reject?reason=${encodeURIComponent(reason || '')}`),
  markReimbursementPaid: (id, creditedDate) => api.post(`/hr/reimbursements/${id}/pay${creditedDate ? `?credited_date=${encodeURIComponent(creditedDate)}` : ''}`),
  deleteReimbursement: (id) => api.delete(`/hr/reimbursements/${id}`),
  // Form 16 (HR)
  getForm16: (params) => api.get('/hr/form16', { params }),
  uploadForm16: (empNo, fy, file) => {
    const fd = new FormData();
    fd.append('employee_number', empNo);
    fd.append('fiscal_year', fy);
    fd.append('file', file);
    return api.post('/hr/form16', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteForm16: (id) => api.delete(`/hr/form16/${id}`),
  // Company profile
  getCompanyProfile: () => api.get('/hr/company-profile'),
  updateCompanyProfile: (data) => api.put('/hr/company-profile', data),
  // Assets
  getAssets: (params) => api.get('/hr/assets', { params }),
  createAsset: (data) => api.post('/hr/assets', data),
  updateAsset: (id, data) => api.put(`/hr/assets/${id}`, data),
  returnAsset: (id, condition) => api.post(`/hr/assets/${id}/return?condition=${encodeURIComponent(condition || 'good')}`),
  deleteAsset: (id) => api.delete(`/hr/assets/${id}`),
  // Tax
  getTaxDeclaration: (empNo, fy) => api.get(`/hr/tax/${empNo}`, { params: { fiscal_year: fy || '2025-26' } }),
  saveTaxDeclaration: (data) => api.post('/hr/tax', data),
  // Employee Edit
  updateEmployee: (empNo, data) => api.put(`/hr/employees/${empNo}`, data),
  createEmployee: (data) => api.post('/hr/employees/create', data),
  // Regularization (Admin)
  getRegularizations: (params) => api.get('/hr/regularizations', { params }),
  approveRegularization: (id) => api.put(`/hr/regularizations/${id}/approve`),
  rejectRegularization: (id) => api.put(`/hr/regularizations/${id}/reject`),
  // Exit Management
  initiateExit: (empNo, data) => api.post(`/hr/employees/${empNo}/initiate-exit`, data),
  cancelExit: (empNo) => api.post(`/hr/employees/${empNo}/cancel-exit`),
  editExit: (empNo, data) => api.put(`/hr/employees/${empNo}/edit-exit`, data),
  processExit: (empNo, data) => api.put(`/hr/employees/${empNo}/process-exit`, data),
  completeExit: (empNo) => api.post(`/hr/employees/${empNo}/complete-exit`),
  getExitTracker: () => api.get('/hr/exit-tracker'),
  // Appraisals
  recordAppraisal: (empNo, data) => api.post(`/hr/employees/${empNo}/appraisal`, data),
  getAppraisalsDue: () => api.get('/hr/appraisals-due'),
  getAppraisals: (params) => api.get('/hr/appraisals', { params }),
  // Salary Slip
  downloadSalarySlip: async (empNo, month) => {
    const res = await api.get(`/hr/salary-slip/${empNo}`, { params: { month }, responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `SalarySlip_${empNo}_${month.replace(/ /g, '_')}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  getSalaryHistory: (empNo) => api.get(`/employee/salary-history/${empNo}`),
  setSalary: (empNo, data) => api.post(`/hr/employees/${empNo}/set-salary`, data),
  getSalary: (empNo) => api.get(`/hr/employees/${empNo}/salary`),
};

// ─── EMPLOYEE PORTAL API ────────────────────────────────────────────────────
export const employeeAPI = {
  login: (employee_number, password) => empApi.post('/employee/auth/login', { employee_number, password }),
  emailLogin: (email, password) => empApi.post('/employee/auth/email-login', { email, password }),
  setPassword: (data) => api.post('/employee/auth/set-password', data),
  bulkSetPasswords: (defaultPassword) => api.post(`/employee/auth/bulk-set-passwords?default_password=${encodeURIComponent(defaultPassword || 'hearclear@123')}`),
  changePassword: (data) => empApi.post('/employee/auth/change-password', data),
  getProfile: () => empApi.get('/employee/me'),
  updateAddress: (data) => empApi.put('/employee/me/address', data),
  getDocuments: () => empApi.get('/employee/me/documents'),
  getPayroll: () => empApi.get('/employee/my-payroll'),
  getLeaves: () => empApi.get('/employee/my-leaves'),
  applyLeave: (data) => empApi.post('/employee/my-leaves/apply', data),
  cancelLeave: (leaveId) => empApi.post(`/employee/my-leaves/${leaveId}/cancel`),
  getAttendance: (month) => empApi.get('/employee/my-attendance', { params: month ? { month } : {} }),
  checkIn: (data) => empApi.post('/employee/check-in', data),
  checkOut: (data) => empApi.post('/employee/check-out', data),
  getReimbursements: () => empApi.get('/employee/my-reimbursements'),
  submitReimbursement: (data) => empApi.post('/employee/my-reimbursements', data),
  deleteReimbursement: (claimId) => empApi.delete(`/employee/my-reimbursements/${claimId}`),
  getAssets: () => empApi.get('/employee/my-assets'),
  // Regularization (Employee)
  requestRegularization: (data) => empApi.post('/employee/attendance/regularize', data),
  getRegularizations: () => empApi.get('/employee/attendance/regularizations'),
  getTax: (fy) => empApi.get('/employee/my-tax', { params: { fiscal_year: fy || '2025-26' } }),
  getCompoffs: () => empApi.get('/employee/my-compoffs'),
  requestCompoff: (data) => empApi.post('/employee/my-compoff', data),
  downloadSalarySlip: async (empNo, month) => {
    const res = await empApi.get('/employee/my-salary-slip', { params: { month }, responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `SalarySlip_${empNo}_${month.replace(/ /g, '_')}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  getSalaryHistory: () => empApi.get('/employee/my-salary-history'),
  updateIdentity: (data) => empApi.put('/employee/update-identity', data),
  getIdentityStatus: () => empApi.get('/employee/my-identity-status'),
  // Form 16 (Employee download)
  getForm16: () => empApi.get('/employee/my-form16'),
  downloadForm16: async (formId, label) => {
    const res = await empApi.get(`/employee/my-form16/${formId}/download`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Form16_${label || formId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  // Manager approvals
  getApprovalsPending: () => empApi.get('/employee/approvals/pending'),
  getApprovalLeaves: () => empApi.get('/employee/approvals/leaves'),
  approveLeaveRequest: (id) => empApi.post(`/employee/approvals/leaves/${id}/approve`),
  rejectLeaveRequest: (id, reason) => empApi.post(`/employee/approvals/leaves/${id}/reject`, { reason: reason || '' }),
  getApprovalReimbursements: () => empApi.get('/employee/approvals/reimbursements'),
  approveReimbursementClaim: (id) => empApi.post(`/employee/approvals/reimbursements/${id}/approve`),
  rejectReimbursementClaim: (id, reason) => empApi.post(`/employee/approvals/reimbursements/${id}/reject`, { reason: reason || '' }),
};

export const payrollAPI = {
  getMonthly: (month) => api.get('/hr/payroll/monthly', { params: { month } }),
  getAvailableMonths: () => api.get('/hr/payroll/available-months'),
  getRunStatus: (month) => api.get('/hr/payroll/run-status', { params: { month } }),
  generatePayroll: (month) => api.post('/hr/payroll/generate', { month }),
  uploadPayrollExcel: (month, file) => {
    const fd = new FormData();
    fd.append('month', month);
    fd.append('file', file);
    return api.post('/hr/payroll/upload-excel', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  markStepComplete: (month, step) => api.post('/hr/payroll/step-complete', { month, step }),
  updateEmployeePayroll: (data) => api.put('/hr/payroll/employee-payroll', data),
  recalculatePayroll: (data) => api.post('/hr/payroll/recalculate', data),
  removeEmployee: (month, empNo) => api.delete(`/hr/payroll/remove-employee?month=${month}&employee_number=${empNo}`),
  lockPayroll: (month) => api.post(`/hr/payroll/lock?month=${month}`),
  unlockPayroll: (month) => api.post(`/hr/payroll/unlock?month=${month}`),
  addAdhoc: (data) => api.post('/hr/payroll/adhoc', data),
  deleteAdhoc: (id) => api.delete(`/hr/payroll/adhoc/${id}`),
};

export const socialAPI = {
  getAnnouncements: () => api.get('/hr/social/announcements'),
  createAnnouncement: (data) => api.post('/hr/social/announcements', data),
  deleteAnnouncement: (id) => api.delete(`/hr/social/announcements/${id}`),
  getGallery: () => api.get('/hr/social/gallery'),
  uploadGalleryPhoto: (formData) => api.post('/hr/social/gallery', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteGalleryPhoto: (id) => api.delete(`/hr/social/gallery/${id}`),
  getCelebrations: () => api.get('/hr/social/celebrations'),
};

// ─── CLINIC PORTAL API ──────────────────────────────────────────────────
export const clinicAuthAPI = {
  login: (username, password) => clinicApi.post('/clinic/login', { username, password }),
  emailLogin: (email, password) => clinicApi.post('/employee/auth/email-login', { email, password }),
  getMe: () => clinicApi.get('/clinic/me'),
  getAudiologists: () => clinicApi.get('/clinic/audiologists'),
  getMyInventory: (params) => clinicApi.get('/clinic/my-inventory', { params }),
  getAllClinicsBrief: () => clinicApi.get('/clinic/all-clinics-brief'),
  getLeadSources: () => clinicApi.get('/lead-sources'),
  getNextSONumber: () => clinicApi.get('/clinic/next-so-number'),
  getNextInvoiceNumber: () => clinicApi.get('/clinic/next-invoice-number'),
};

export const clinicAdminAPI = {
  listClinics: () => api.get('/clinic/admin/list'),
  createClinic: (data) => api.post('/clinic/admin/create', data),
  updateClinic: (id, data) => api.put(`/clinic/admin/${id}`, data),
  resetPassword: (id) => api.post(`/clinic/admin/${id}/reset-password`),
};

export const billingAPI = {
  createInvoice: (data) => clinicApi.post('/billing/create', data),
  getInvoices: (params) => clinicApi.get('/billing/invoices', { params }),
  getInvoice: (id) => clinicApi.get(`/billing/invoices/${id}`),
  getRevenueSummary: (params) => clinicApi.get('/billing/revenue-summary', { params }),
  getStates: () => clinicApi.get('/billing/states'),
  getInvoicePdf: (id) => clinicApi.get(`/billing/invoices/${id}/pdf`, { responseType: 'blob' }),
  downloadInvoicePdf: async (id, invoiceNumber) => {
    const res = await clinicApi.get(`/billing/invoices/${id}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${(invoiceNumber || 'invoice').replace(/\//g, '-')}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  viewInvoicePdf: async (id) => {
    const res = await clinicApi.get(`/billing/invoices/${id}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    window.open(url, '_blank');
  },
  // Admin endpoints (use admin api)
  adminGetInvoices: (params) => api.get('/billing/admin/invoices', { params }),
  adminGetRevenue: (params) => api.get('/billing/admin/revenue', { params }),
  adminViewInvoicePdf: async (id) => {
    const res = await api.get(`/billing/admin/invoices/${id}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    window.open(url, '_blank');
  },
  viewCreditNotePdf: async (id) => {
    const res = await clinicApi.get(`/billing/credit-notes/${id}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    window.open(url, '_blank');
  },
  adminViewCreditNotePdf: async (id) => {
    const res = await api.get(`/billing/admin/credit-notes/${id}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    window.open(url, '_blank');
  },
  // Sales Orders (clinic)
  createSalesOrder: (data) => clinicApi.post('/billing/sales-orders/create', data),
  editSalesOrder: (id, data) => clinicApi.put(`/billing/sales-orders/${id}`, data),
  getSalesOrders: (params) => clinicApi.get('/billing/sales-orders', { params }),
  getSalesOrder: (id) => clinicApi.get(`/billing/sales-orders/${id}`),
  convertSOToInvoice: (id, data) => clinicApi.post(`/billing/sales-orders/${id}/convert-to-invoice`, data),
  updateSOStatus: (id, data) => clinicApi.put(`/billing/sales-orders/${id}/status`, data),
  // Payments (clinic)
  recordPayment: (params, data) => clinicApi.post('/billing/payments/record', data, { params }),
  getPayments: (params) => clinicApi.get('/billing/payments/list', { params }),
  getBanks: () => clinicApi.get('/banks'),
  getInventoryInHand: (params) => clinicApi.get('/clinic/inventory-in-hand', { params }),
  getPaymentModes: () => clinicApi.get('/payment-modes'),
  getCustomerCreditBalance: (id) => clinicApi.get(`/billing/customers/${id}/credit-balance`),
  applyCredit: (invoiceId, data) => clinicApi.post(`/billing/invoices/${invoiceId}/apply-credit`, data || {}),
  getAvailableSerials: (params) => clinicApi.get('/inventory/available-serials', { params }),
  dissociatePayment: (paymentId) => clinicApi.post(`/billing/payments/${paymentId}/dissociate-credit`),
  refundPayment: (paymentId, data) => clinicApi.post(`/billing/payments/${paymentId}/refund`, data),
  // Cash ledger (clinic)
  getCashLedger: (params) => clinicApi.get('/clinic/cash-ledger', { params }),
  addCashExpense: (data) => clinicApi.post('/clinic/cash-ledger/expense', data),
  setCashOpening: (data) => clinicApi.post('/clinic/cash-ledger/opening', data),
  depositCash: (data) => clinicApi.post('/clinic/cash-ledger/deposit', data),
  uploadCashReceipt: (formData) => clinicApi.post('/clinic/cash-ledger/upload-receipt', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  viewCashReceipt: async (path) => {
    const res = await clinicApi.get('/clinic/cash-ledger/receipt', { params: { path }, responseType: 'blob' });
    const url = window.URL.createObjectURL(res.data);
    window.open(url, '_blank');
  },
  getPaymentReceiptPdf: async (paymentId) => {
    const res = await clinicApi.get(`/billing/payments/${paymentId}/receipt-pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    window.open(url, '_blank');
  },
  // Customers (clinic)
  getCustomers: (params) => clinicApi.get('/billing/customers', { params }),
  getCustomerProfile: (id) => clinicApi.get(`/billing/customers/${id}`),
  createCustomer: (data) => clinicApi.post('/billing/customers', data),
  recordAdvance: (data) => clinicApi.post('/billing/customer-advance', data),
  // Admin Sales Orders
  adminGetSalesOrders: (params) => api.get('/billing/admin/sales-orders', { params }),
  adminExportSalesOrders: (params) => api.get('/billing/admin/sales-orders/export', { params, responseType: 'blob' }),
  adminGetSalesOrder: (id) => api.get(`/billing/admin/sales-orders/${id}`),
  adminBulkUploadSO: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/billing/admin/sales-orders/bulk-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  adminDownloadSOTemplate: () => api.get('/billing/admin/sales-orders/bulk-upload/template', { responseType: 'blob' }),
  adminUpdateSOStatus: (id, data) => api.put(`/billing/admin/sales-orders/${id}/status`, data),
  adminEditSO: (id, data) => api.put(`/billing/admin/sales-orders/${id}`, data),
  adminDeleteSO: (id) => api.delete(`/billing/admin/sales-orders/${id}`),
  adminBulkDeleteSOs: (ids) => api.post('/billing/admin/sales-orders/bulk-delete', { ids }),
  adminBulkDeleteInvoices: (ids) => api.post('/admin/invoices/bulk-delete', { ids }),
  adminGetBranches: () => api.get('/billing/admin/branches'),
  adminOrderInventorySummary: () => api.get('/billing/admin/order-inventory-summary'),
  // Credit Notes (clinic)
  createCreditNote: (data) => clinicApi.post('/billing/credit-notes/create', data),
  getCreditNotes: (params) => clinicApi.get('/billing/credit-notes', { params }),
  getCreditNote: (id) => clinicApi.get(`/billing/credit-notes/${id}`),
  // Credit Notes (admin)
  adminGetCreditNotes: (params) => api.get('/billing/admin/credit-notes', { params }),
  adminExportCreditNotes: (params) => api.get('/billing/admin/credit-notes/export', { params, responseType: 'blob' }),
  adminBulkDeleteCreditNotes: (ids) => api.post('/billing/admin/credit-notes/bulk-delete', { ids }),
  adminCreateCreditNote: (data) => api.post('/admin/credit-notes/create', data),
  adminEditCreditNote: (id, data) => api.put(`/billing/admin/credit-notes/${id}`, data),
  adminDeleteCreditNote: (id) => api.delete(`/billing/admin/credit-notes/${id}`),
  // Analytics (admin)
  adminGetAnalytics: (params) => api.get('/billing/admin/analytics', { params }),
};

export const inventoryAPI = {
  getDashboard: () => api.get('/inventory/dashboard'),
  getItems: (params) => api.get('/inventory/items', { params }),
  getItem: (id) => api.get(`/inventory/items/${id}`),
  createItem: (data) => api.post('/inventory/items', data),
  updateItem: (id, data) => api.put(`/inventory/items/${id}`, data),
  deleteItem: (id) => api.delete(`/inventory/items/${id}`),
  importFromZoho: () => api.post('/inventory/import-zoho'),
  getStock: (params) => api.get('/inventory/stock', { params }),
  updateStock: (data) => api.post('/inventory/stock-update', data),
  transferStock: (data) => api.post('/inventory/transfer', data),
  getLocations: () => api.get('/inventory/locations'),
  getCategories: () => api.get('/inventory/categories'),
};

// Billing Operations (Invoice creation, vendor bills, transfer orders, margin)
export const billingOpsAPI = {
  // Invoice creation
  createInvoice: (data) => api.post('/admin/invoices/create', data),
  recordPayment: (invoiceId, data) => api.post(`/admin/invoices/${invoiceId}/record-payment`, data),
  recordAdvance: (data) => api.post('/admin/customer-advance', data),
  convertibleSOs: (params) => api.get('/admin/convertible-sales-orders', { params }),
  // Vendor bills
  createVendorBill: (data) => api.post('/admin/vendor-bills/create', data),
  updateVendorBill: (id, data) => api.put(`/admin/vendor-bills/${id}`, data),
  listVendorBills: (params) => api.get('/admin/vendor-bills', { params }),
  exportVendorBills: (params) => api.get('/admin/vendor-bills/export', { params, responseType: 'blob' }),
  deleteVendorBill: (id) => api.delete(`/admin/vendor-bills/${id}`),
  bulkDeleteVendorBills: (ids) => api.post('/admin/vendor-bills/bulk-delete', { ids }),
  bulkUploadVendorBills: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/admin/vendor-bills/bulk-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  vendorBillsTemplate: () => api.get('/admin/vendor-bills/bulk-upload/template', { responseType: 'blob' }),
  // Vendor credits (stock returns to OEM)
  createVendorCredit: (data) => api.post('/admin/vendor-credits/create', data),
  listVendorCredits: (params) => api.get('/admin/vendor-credits', { params }),
  getVendorCredit: (id) => api.get(`/admin/vendor-credits/${id}`),
  editVendorCredit: (id, data) => api.put(`/admin/vendor-credits/${id}`, data),
  deleteVendorCredit: (id) => api.delete(`/admin/vendor-credits/${id}`),
  bulkUploadVendorCredits: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/admin/vendor-credits/bulk-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  vendorCreditNextNumber: (branchId) => api.get('/admin/vendor-credits/next-number', { params: { branch_id: branchId } }),
  uploadVendorBillPDF: (file, branchId, vendorName) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/admin/vendor-bills/upload-pdf?branch_id=${branchId}&vendor_name=${encodeURIComponent(vendorName)}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // Transfer orders
  createTransferOrder: (data) => api.post('/admin/transfer-orders/create', data),
  listTransferOrders: (params) => api.get('/admin/transfer-orders', { params }),
  dedupeTransferOrders: () => api.post('/admin/transfer-orders/dedupe'),
  dedupeInvoiceNumbers: () => api.post('/admin/invoices/dedupe-numbers'),
  bulkUploadTransferOrders: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/admin/transfer-orders/bulk-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  downloadTransferOrderTemplate: () => api.get('/admin/transfer-orders/bulk-upload/template', { responseType: 'blob' }),
  // Purchase orders (#7)
  createPurchaseOrder: (data) => api.post('/admin/purchase-orders/create', data),
  listPurchaseOrders: (params) => api.get('/admin/purchase-orders', { params }),
  getPurchaseOrder: (id) => api.get(`/admin/purchase-orders/${id}`),
  updatePurchaseOrder: (id, data) => api.put(`/admin/purchase-orders/${id}`, data),
  deletePurchaseOrder: (id) => api.delete(`/admin/purchase-orders/${id}`),
  convertPOToBill: (id, data) => api.post(`/admin/purchase-orders/${id}/convert-to-bill`, data),
  // Payment mode master (#3)
  listPaymentModes: () => api.get('/payment-modes'),
  listAllPaymentModes: () => api.get('/admin/payment-modes'),
  createPaymentMode: (data) => api.post('/admin/payment-modes', data),
  updatePaymentMode: (id, data) => api.put(`/admin/payment-modes/${id}`, data),
  deletePaymentMode: (id) => api.delete(`/admin/payment-modes/${id}`),
  // Margin tracker
  getCOGSMaster: () => api.get('/admin/margin/cogs-master'),
  updateCOGSMaster: (data) => api.post('/admin/margin/cogs-master', data),
  getMarginReport: (params) => api.get('/admin/margin/report', { params }),
};

// Lead Sources API
export const leadSourcesAPI = {
  list: () => api.get('/lead-sources'),
  create: (data) => api.post('/lead-sources', data),
  update: (id, data) => api.put(`/lead-sources/${id}`, data),
  delete: (id) => api.delete(`/lead-sources/${id}`),
  seed: () => api.post('/lead-sources/seed'),
};

// Items Master API
export const itemsAPI = {
  list: (params) => api.get('/items', { params }),
  get: (id) => api.get(`/items/${id}`),
  create: (data) => api.post('/items', data),
  update: (id, data) => api.put(`/items/${id}`, data),
  delete: (id) => api.delete(`/items/${id}`),
  categories: () => api.get('/items/categories'),
  bulkUpload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/items/bulk-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  downloadTemplate: () => api.get('/items/download/template'),
  inventoryDetail: (id) => api.get(`/items/${id}/inventory`),
  addOpeningStock: (id, data) => api.post(`/items/${id}/opening-stock`, data),
  deleteSerial: (serialId) => api.delete(`/items/serials/${serialId}`),
  removeDummySerials: (itemId, serialUids) => api.post(`/items/serials/remove-dummy`, { serial_uids: serialUids || [] }, { params: itemId ? { item_id: itemId } : {} }),
};

// Location Master API
export const locationsAPI = {
  list: () => api.get('/locations'),
  get: (id) => api.get(`/locations/${id}`),
  create: (data) => api.post('/locations', data),
  update: (id, data) => api.put(`/locations/${id}`, data),
  delete: (id) => api.delete(`/locations/${id}`),
  states: () => api.get('/locations/states'),
  addSeries: (id, data) => api.post(`/locations/${id}/series`, data),
  updateSeries: (id, seriesId, data) => api.put(`/locations/${id}/series/${seriesId}`, data),
  deleteSeries: (id, seriesId) => api.delete(`/locations/${id}/series/${seriesId}`),
};

export default api;
